import React, { useState, useRef, useEffect } from 'react';
import { Supplier, SupplierAttachment } from '../../../types/supplier';
import { v4 as uuidv4 } from 'uuid';

interface SupplierCardProps {
  supplier: Supplier;
  isNew: boolean;
  onCreate: (supplier: Supplier) => void;
  onUpdate: (supplier: Supplier) => void;
  onDelete: (id: string) => void;
  isExpanded: boolean;
  onToggleExpand: (supplierId: string) => void;
  onRegisterUnsavedCheck: (checkFn: () => boolean) => void;
  requestedSupplierId: string | null;
  onConfirmSwitch: () => void;
  onCancelSwitch: () => void;
  highlightOnMount?: boolean;
}

export const SupplierCard: React.FC<SupplierCardProps> = ({
  supplier,
  isNew,
  onCreate,
  onUpdate,
  onDelete,
  isExpanded,
  onToggleExpand,
  onRegisterUnsavedCheck,
  requestedSupplierId,
  onConfirmSwitch,
  onCancelSwitch,
  highlightOnMount = false
}) => {
  const [isHighlighted, setIsHighlighted] = useState(highlightOnMount);
  const cardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ingredientInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  // File size limits
  const MAX_FILE_SIZE_MB = 2;
  const MAX_TOTAL_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedSupplier, setEditedSupplier] = useState<Supplier>(supplier);
  const [originalSupplier, setOriginalSupplier] = useState<Supplier>(supplier);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    'collapse' | 'cancel' | { type: 'switch'; targetId: string } | null
  >(null);

  // Local state for ingredient rows - initialize with one empty row if array is empty
  const [ingredientRows, setIngredientRows] = useState<string[]>(() => {
    return supplier.ingredientsSupplied.length > 0 ? supplier.ingredientsSupplied : [''];
  });

  // Update ingredient rows when supplier changes (e.g., from external update)
  useEffect(() => {
    const newRows = supplier.ingredientsSupplied.length > 0 ? supplier.ingredientsSupplied : [''];
    setIngredientRows(newRows);
    setEditedSupplier(supplier);
    setOriginalSupplier(supplier);
  }, [supplier]); // Update when supplier changes

  // Register unsaved changes check with parent
  useEffect(() => {
    if (isExpanded) {
      onRegisterUnsavedCheck(hasUnsavedChanges);
    } else {
      onRegisterUnsavedCheck(() => false);
    }
  }, [isExpanded, isEditMode, editedSupplier, originalSupplier, onRegisterUnsavedCheck]);

  // Watch for switch requests from parent
  useEffect(() => {
    if (requestedSupplierId && hasUnsavedChanges()) {
      setPendingAction({ type: 'switch', targetId: requestedSupplierId });
      setShowUnsavedModal(true);
    }
  }, [requestedSupplierId]);

  // Auto-enter edit mode for new blank suppliers
  useEffect(() => {
    if (isExpanded && !supplier.supplierName && !isEditMode) {
      setIsEditMode(true);
    }
  }, [isExpanded, supplier.supplierName, isEditMode]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!isEditMode) return false;
    return JSON.stringify(editedSupplier) !== JSON.stringify(originalSupplier);
  };

  const handleFieldChange = (field: keyof Supplier, value: string) => {
    if (isEditMode) {
      // In edit mode, update local state only
      setEditedSupplier({ ...editedSupplier, [field]: value });
    } else {
      // Not in edit mode, save directly to Redux (shouldn't happen with disabled fields)
      onUpdate({ ...supplier, [field]: value });
    }
  };

  const handleIngredientChange = (index: number, value: string) => {
    const newRows = [...ingredientRows];
    newRows[index] = value;
    setIngredientRows(newRows);

    if (isEditMode) {
      // In edit mode, update local state only
      const filteredIngredients = newRows.filter(row => row.trim().length > 0);
      setEditedSupplier({ ...editedSupplier, ingredientsSupplied: filteredIngredients });
    } else {
      // Not in edit mode, save directly to Redux (shouldn't happen with disabled fields)
      const filteredIngredients = newRows.filter(row => row.trim().length > 0);
      onUpdate({ ...supplier, ingredientsSupplied: filteredIngredients });
    }
  };

  const handleAddIngredient = () => {
    // Add new empty field at the TOP of the list
    setIngredientRows(['', ...ingredientRows]);
    // Focus the new field at index 0
    setFocusIndex(0);
  };

  const handleIngredientKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      // Add new empty field at the TOP and focus it
      setIngredientRows(['', ...ingredientRows]);
      setFocusIndex(0);
    }
  };

  const handleRemoveIngredient = (index: number) => {
    const newRows = ingredientRows.filter((_, i) => i !== index);
    // Ensure at least one row remains
    const finalRows = newRows.length > 0 ? newRows : [''];
    setIngredientRows(finalRows);

    if (isEditMode) {
      // In edit mode, update local state only
      const filteredIngredients = finalRows.filter(row => row.trim().length > 0);
      setEditedSupplier({ ...editedSupplier, ingredientsSupplied: filteredIngredients });
    } else {
      // Not in edit mode, save directly to Redux (shouldn't happen with disabled fields)
      const filteredIngredients = finalRows.filter(row => row.trim().length > 0);
      onUpdate({ ...supplier, ingredientsSupplied: filteredIngredients });
    }
  };

  const handleDelete = () => {
    const displayName = supplier.supplierName || 'this supplier';
    if (window.confirm(`Are you sure you want to delete ${displayName}?`)) {
      onDelete(supplier.id);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setOriginalSupplier(supplier);
    setEditedSupplier(supplier);
  };

  const handleSave = () => {
    // Validate supplier name is not empty
    if (!editedSupplier.supplierName || editedSupplier.supplierName.trim() === '') {
      alert('Supplier name is required. Please enter a supplier name before saving.');
      return;
    }

    // Call onCreate for new suppliers, onUpdate for existing ones
    if (isNew) {
      onCreate(editedSupplier);
    } else {
      onUpdate(editedSupplier);
    }

    setIsEditMode(false);
    setOriginalSupplier(editedSupplier);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setPendingAction('cancel');
      setShowUnsavedModal(true);
    } else {
      // No changes, just exit edit mode
      setIsEditMode(false);
      setEditedSupplier(supplier);
      setIngredientRows(supplier.ingredientsSupplied.length > 0 ? supplier.ingredientsSupplied : ['']);
    }
  };

  const handleToggleExpand = () => {
    if (isExpanded && hasUnsavedChanges()) {
      // Trying to collapse with unsaved changes
      setPendingAction('collapse');
      setShowUnsavedModal(true);
    } else {
      onToggleExpand(supplier.id);
    }
  };

  const handleModalDiscard = () => {
    setShowUnsavedModal(false);
    setIsEditMode(false);
    setEditedSupplier(supplier);
    setIngredientRows(supplier.ingredientsSupplied.length > 0 ? supplier.ingredientsSupplied : ['']);

    if (pendingAction === 'collapse') {
      // Discard changes and collapse
      onToggleExpand(supplier.id);
    } else if (pendingAction && typeof pendingAction === 'object' && pendingAction.type === 'switch') {
      // Discard changes and switch to target supplier
      onConfirmSwitch();
    }

    setPendingAction(null);
  };

  const handleModalCancel = () => {
    setShowUnsavedModal(false);

    if (pendingAction && typeof pendingAction === 'object' && pendingAction.type === 'switch') {
      onCancelSwitch();
    }

    setPendingAction(null);
  };

  // File attachment handlers
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const currentTotalSize = (editedSupplier.attachments || []).reduce((sum, att) => sum + att.fileSize, 0);

    Array.from(files).forEach(file => {
      // Validate file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
        return;
      }

      // Check total size
      if (currentTotalSize + file.size > MAX_TOTAL_SIZE_BYTES) {
        alert(`Adding "${file.name}" would exceed ${MAX_TOTAL_SIZE_MB}MB total limit.`);
        return;
      }

      // Read file as data URL
      const reader = new FileReader();
      reader.onload = e => {
        const dataUrl = e.target?.result as string;

        const newAttachment: SupplierAttachment = {
          id: uuidv4(),
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          dataUrl: dataUrl,
          uploadedAt: new Date().toISOString()
        };

        setEditedSupplier({
          ...editedSupplier,
          attachments: [...(editedSupplier.attachments || []), newAttachment]
        });
      };

      reader.onerror = () => {
        alert(`Failed to read file "${file.name}".`);
      };

      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = (attachment: SupplierAttachment) => {
    const link = document.createElement('a');
    link.href = attachment.dataUrl;
    link.download = attachment.fileName;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    if (!isEditMode) return;

    const attachment = editedSupplier.attachments?.find(a => a.id === attachmentId);
    if (!attachment) return;

    if (window.confirm(`Remove "${attachment.fileName}"?`)) {
      setEditedSupplier({
        ...editedSupplier,
        attachments: editedSupplier.attachments?.filter(a => a.id !== attachmentId) || []
      });
    }
  };

  const createdDate = new Date(supplier.createdAt).toLocaleDateString();
  const approvalDateFormatted = supplier.approvalDate ? new Date(supplier.approvalDate).toLocaleDateString() : '';

  // Format contact info for collapsed view
  const formatContactInfo = () => {
    const parts = [];
    if (supplier.contactName) parts.push(supplier.contactName);
    if (supplier.contactEmail) parts.push(supplier.contactEmail);
    return parts.length > 0 ? parts.join(' • ') : 'No contact info';
  };

  // Handle highlighting and scrolling when component mounts with highlightOnMount
  useEffect(() => {
    if (highlightOnMount) {
      // Scroll to this card
      cardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Remove highlight after 3 seconds
      const timer = setTimeout(() => {
        setIsHighlighted(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [highlightOnMount]);

  // Auto-focus ingredient input when focusIndex changes
  useEffect(() => {
    if (focusIndex !== null && ingredientInputRefs.current[focusIndex]) {
      ingredientInputRefs.current[focusIndex]?.focus();
      setFocusIndex(null);
    }
  }, [focusIndex]);

  return (
    <div
      ref={cardRef}
      style={{
        border: `2px solid ${isHighlighted ? '#2196F3' : '#ddd'}`,
        borderRadius: '8px',
        marginBottom: '16px',
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        boxShadow: isHighlighted ? '0 0 20px rgba(33, 150, 243, 0.5)' : 'none'
      }}
    >
      {/* Header */}
      <div
        onClick={handleToggleExpand}
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: isExpanded ? '1px solid #ddd' : 'none'
        }}
      >
        <div style={{ flex: 1 }}>
          <h3
            style={{
              margin: '0 0 8px 0',
              fontSize: '1.2rem'
            }}
          >
            {supplier.supplierName || 'New Supplier'}
          </h3>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            <span>{formatContactInfo()}</span>
            {approvalDateFormatted && (
              <>
                <span style={{ margin: '0 12px' }}>•</span>
                <span>Approved {approvalDateFormatted}</span>
              </>
            )}
            <span style={{ margin: '0 12px' }}>•</span>
            <span>Added {createdDate}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '1.5rem', color: '#666' }}>{isExpanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: '20px' }}>
          {/* Supplier Info */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '20px'
            }}
          >
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>
                Supplier Name <span style={{ color: '#f44336' }}>*</span>
              </label>
              <input
                type="text"
                value={editedSupplier.supplierName}
                onChange={e => handleFieldChange('supplierName', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>
                Supplier Code
              </label>
              <input
                type="text"
                value={editedSupplier.supplierCode}
                onChange={e => handleFieldChange('supplierCode', e.target.value)}
                disabled={!isEditMode}
                maxLength={10}
                placeholder="e.g., SUP001"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <h4 style={{ margin: '12px 0 8px 0', fontSize: '1rem', color: '#555' }}>Contact Information</h4>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Name</label>
              <input
                type="text"
                value={editedSupplier.contactName || ''}
                onChange={e => handleFieldChange('contactName', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Email</label>
              <input
                type="email"
                value={editedSupplier.contactEmail || ''}
                onChange={e => handleFieldChange('contactEmail', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Phone Number</label>
              <input
                type="tel"
                value={editedSupplier.contactPhone || ''}
                onChange={e => handleFieldChange('contactPhone', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Contact Information</label>
              <input
                type="text"
                value={editedSupplier.contactInformation}
                onChange={e => handleFieldChange('contactInformation', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Approval Date</label>
              <input
                type="date"
                value={editedSupplier.approvalDate}
                onChange={e => handleFieldChange('approvalDate', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Review Date</label>
              <input
                type="date"
                value={editedSupplier.reviewDate}
                onChange={e => handleFieldChange('reviewDate', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Approved By</label>
              <input
                type="text"
                value={editedSupplier.approvedBy}
                onChange={e => handleFieldChange('approvedBy', e.target.value)}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}
              >
                <label style={{ fontWeight: 'bold' }}>Ingredients Supplied</label>
                <button
                  onClick={handleAddIngredient}
                  type="button"
                  disabled={!isEditMode}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: isEditMode ? '#4CAF50' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isEditMode ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  + Add Ingredient
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {ingredientRows.map((ingredient, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      ref={el => (ingredientInputRefs.current[index] = el)}
                      type="text"
                      value={ingredient}
                      onChange={e => handleIngredientChange(index, e.target.value)}
                      onKeyDown={e => handleIngredientKeyDown(index, e)}
                      placeholder="Enter ingredient name"
                      disabled={!isEditMode}
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                        cursor: isEditMode ? 'text' : 'not-allowed'
                      }}
                    />
                    <button
                      onClick={() => handleRemoveIngredient(index)}
                      type="button"
                      disabled={!isEditMode || ingredientRows.length === 1}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: !isEditMode || ingredientRows.length === 1 ? '#ccc' : '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: !isEditMode || ingredientRows.length === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        minWidth: '36px'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Comments</label>
              <textarea
                value={editedSupplier.comments}
                onChange={e => handleFieldChange('comments', e.target.value)}
                rows={3}
                disabled={!isEditMode}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  backgroundColor: isEditMode ? 'white' : '#f5f5f5',
                  cursor: isEditMode ? 'text' : 'not-allowed'
                }}
              />
            </div>

            {/* File Attachments Section */}
            <div style={{ gridColumn: 'span 2', marginTop: '12px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}
              >
                <label style={{ fontWeight: 'bold' }}>
                  File Attachments
                  {(editedSupplier.attachments?.length || 0) > 0 && (
                    <span
                      style={{
                        marginLeft: '8px',
                        fontSize: '0.9em',
                        color: '#666',
                        fontWeight: 'normal'
                      }}
                    >
                      ({editedSupplier.attachments?.length} file
                      {editedSupplier.attachments?.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </label>

                {isEditMode && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    📎 Attach PDF
                  </button>
                )}
              </div>

              {/* Hidden file input - PDFs only */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,application/pdf"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />

              {/* Attachments List */}
              {(editedSupplier.attachments?.length || 0) > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                >
                  {editedSupplier.attachments?.map(attachment => (
                    <div
                      key={attachment.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#2196F3',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => handleDownloadAttachment(attachment)}
                        >
                          {attachment.fileName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                          {formatFileSize(attachment.fileSize)} • Uploaded{' '}
                          {new Date(attachment.uploadedAt).toLocaleDateString()}
                        </div>
                      </div>

                      {isEditMode && (
                        <button
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          type="button"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            marginLeft: '12px',
                            minWidth: '32px'
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    border: '1px dashed #ddd',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}
                >
                  No attachments
                </div>
              )}

              {/* Help text */}
              {isEditMode && (
                <p
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '8px',
                    fontStyle: 'italic'
                  }}
                >
                  Maximum {MAX_FILE_SIZE_MB}MB per file, {MAX_TOTAL_SIZE_MB}MB total. PDF files only.
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              borderTop: '1px solid #ddd',
              paddingTop: '16px'
            }}
          >
            <button
              onClick={handleDelete}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Delete Supplier
            </button>

            <div style={{ display: 'flex', gap: '12px' }}>
              {isEditMode ? (
                <>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#757575',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Save
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEdit}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={handleModalCancel}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.25rem' }}>Unsaved Changes</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666' }}>
              You have unsaved changes. Do you want to discard them?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleModalCancel}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#757575',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Keep Editing
              </button>
              <button
                onClick={handleModalDiscard}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
